/**
 * The pieces every tool board is built out of: a strip, a field, a divider, a line of trouble.
 *
 * Write and Arrange are the two boards where something is asked of the rules, and both are
 * drawn as an application rather than as a page -- a toolbar of settings, a body that scrolls
 * under it, a bar of state along the bottom. They therefore want the same controls, and a
 * control that is defined twice drifts: one board's select grows a hover the other never gets.
 *
 * The prose pages keep their own kit in `Page.tsx`. Nothing here is for them: a cream notice
 * on dark chrome reads as a piece of another application, which is the whole reason `Trouble`
 * exists beside `Failed` rather than replacing it.
 */

/** A number typed into a toolbar: short, centred, monospaced, because it is a bar number. */
export const FIELD =
  "h-7 w-12 rounded bg-shell-key px-1.5 text-center font-mono text-[11px] text-shell-lit outline-none focus:ring-1 focus:ring-shell-live";

/** A menu on a toolbar. */
export const SELECT =
  "h-7 rounded bg-shell-key px-1.5 text-[11px] text-shell-lit outline-none hover:bg-shell-hot";

/** What a group of controls is called, said quietly enough to be ignored once it is learnt. */
export const LABEL = "font-mono text-[10px] uppercase tracking-[0.12em] text-shell-dim";

/** The strip itself: wraps rather than scrolls, so no control is ever off the right edge. */
export const STRIP =
  "flex flex-wrap items-center gap-x-2.5 gap-y-2 border-b border-shell-edge bg-shell-bar px-2.5 py-1.5";

/** A thing that can be done to what is on the board, quiet until it is pointed at. */
export const ACTION =
  "inline-flex h-7 shrink-0 items-center rounded px-2.5 text-[11px] text-shell-text hover:bg-shell-hot hover:text-shell-lit";

/** The one action a strip is for, where it has one: the same blue as `Run`. */
export const ACTION_LIVE =
  "inline-flex h-7 shrink-0 items-center rounded bg-shell-live px-3 text-[11px] font-semibold text-white hover:brightness-110 disabled:bg-shell-key disabled:text-shell-dim";

/** The thing a toolbar does instead of a heading: separate one group of controls from the next. */
export function Divider(): JSX.Element {
  return <span aria-hidden="true" className="h-5 w-px shrink-0 bg-shell-seam" />;
}

/** Something went wrong, said on the chrome and never in the score's own space. */
export function Trouble({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <p
      role="alert"
      className="shrink-0 border-b border-shell-edge bg-fault/15 px-2.5 py-1.5 text-[11px] leading-relaxed text-shell-lit"
    >
      {children}
    </p>
  );
}

/** The one button on a strip that starts the work, and the only blue thing on it. */
export function Run({
  label,
  disabled,
  title,
  onRun,
}: {
  /** Already says whether it is running: the caller knows what its own verb is. */
  readonly label: string;
  readonly disabled: boolean;
  /** Why it is dead, where it is dead for a reason the reader can put right. */
  readonly title?: string;
  onRun(): void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onRun}
      disabled={disabled}
      title={title}
      className="ml-auto h-7 rounded bg-shell-live px-4 text-[11px] font-semibold text-white hover:brightness-110 disabled:bg-shell-key disabled:text-shell-dim"
    >
      {label}
    </button>
  );
}
